import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';

import type { Flow, ResponsePayload } from '../../types';
import { useEventTracking } from 'hooks/useEventTracking';

const FAST_ANSWER_THRESHOLD_MS = 3000;
const FAST_ANSWER_STREAK_TARGET = 3;
const FLOW_HEARTBEAT_INTERVAL_MS = 15_000;

export interface UseQuestionFlowAnalyticsParams {
  flow: Flow | null;
  flowName: string;
  flowRunId: number | null;
  currentQuestionIndex: number;
}

interface FlowContinueEventParams {
  questionId: number;
  targetQuestionIndex: number;
  currentQuestionIndex: number;
  questionText?: string | null;
  timeSpentMs: number;
}

interface FlowSubmitEventParams {
  response: ResponsePayload;
  targetQuestionIndex: number;
  totalQuestions: number;
  answeredQuestions: number;
  timeSpentMs: number;
  currentQuestionIndex: number;
}

interface FlowPreviousEventParams {
  questionId: number | null;
  questionIndex: number;
  currentQuestionIndex: number;
  questionText?: string | null;
  timeSpentMs: number;
}

interface EvaluateSpeedWarningParams {
  isAdvancing: boolean;
  timeSpentMs: number;
  isStaff: boolean;
}

export interface UseQuestionFlowAnalyticsResult {
  markQuestionStart: (questionId: number | null) => void;
  getQuestionDuration: () => number;
  evaluateSpeedWarning: (params: EvaluateSpeedWarningParams) => boolean;
  trackFlowStepView: (params: { questionId: number; questionIndex: number; questionText?: string | null }) => void;
  trackFlowContinue: (params: FlowContinueEventParams) => void;
  trackFlowSubmit: (params: FlowSubmitEventParams) => void;
  trackFlowPrevious: (params: FlowPreviousEventParams) => void;
}

const toMillisSince = (startedAtRef: MutableRefObject<number>): number => {
  return Math.max(Date.now() - startedAtRef.current, 0);
};

export const useQuestionFlowAnalytics = ({
  flow,
  flowName,
  flowRunId,
  currentQuestionIndex,
}: UseQuestionFlowAnalyticsParams): UseQuestionFlowAnalyticsResult => {
  const { track } = useEventTracking();
  const flowEntryTrackedRef = useRef<string | null>(null);
  const flowStartTimeRef = useRef<number | null>(null);
  const questionStartTimeRef = useRef<number>(Date.now());
  const lastQuestionIdRef = useRef<number | null>(null);
  const consecutiveFastAnswersRef = useRef(0);
  const activeFlowSessionKeyRef = useRef<string | null>(null);
  const exitedFlowSessionKeyRef = useRef<string | null>(null);
  const visibleWindowStartedAtRef = useRef<number | null>(null);
  const lastTrackedVisibleStepKeyRef = useRef<string | null>(null);
  const visibilityStateRef = useRef<DocumentVisibilityState>(
    typeof document !== 'undefined' ? document.visibilityState : 'visible',
  );
  const flowEventContextRef = useRef<{
    flowId: number | null;
    flowName: string;
    flowRunId: number | null;
    currentQuestionIndex: number;
  }>({
    flowId: null,
    flowName,
    flowRunId: null,
    currentQuestionIndex,
  });

  const resolvedFlowId = flow?.id ?? null;
  const resolvedFlowName = flow?.name ?? flowName;

  useEffect(() => {
    flowEventContextRef.current = {
      flowId: resolvedFlowId,
      flowName: resolvedFlowName,
      flowRunId,
      currentQuestionIndex,
    };
  }, [currentQuestionIndex, flowRunId, resolvedFlowId, resolvedFlowName]);

  const getFlowEventMetadata = useCallback(() => {
    const context = flowEventContextRef.current;
    return {
      flowId: context.flowId,
      flowName: context.flowName,
      flowRunId: context.flowRunId,
      currentQuestionIndex: context.currentQuestionIndex,
    };
  }, []);

  const flushActiveVisibleTime = useCallback(
    (
      eventType: 'flow_heartbeat' | 'flow_visibility_changed' | 'flow_exit',
      metadata: Record<string, any> = {},
    ) => {
      if (!activeFlowSessionKeyRef.current) {
        return;
      }

      const now = Date.now();
      const visibleStart = visibleWindowStartedAtRef.current;
      const activeVisibleMs =
        visibilityStateRef.current === 'visible' && visibleStart != null
          ? Math.max(now - visibleStart, 0)
          : 0;

      if (eventType === 'flow_heartbeat' && activeVisibleMs <= 0) {
        return;
      }

      track(eventType, {
        ...getFlowEventMetadata(),
        activeVisibleMs,
        ...metadata,
      });

      if (visibilityStateRef.current === 'visible') {
        visibleWindowStartedAtRef.current = now;
      }
    },
    [getFlowEventMetadata, track],
  );

  const emitFlowExit = useCallback(
    (reason: string) => {
      const currentSessionKey = activeFlowSessionKeyRef.current;
      if (!currentSessionKey) {
        return;
      }

      if (exitedFlowSessionKeyRef.current === currentSessionKey) {
        return;
      }

      exitedFlowSessionKeyRef.current = currentSessionKey;
      flushActiveVisibleTime('flow_exit', { reason });
      activeFlowSessionKeyRef.current = null;
      visibleWindowStartedAtRef.current = null;
    },
    [flushActiveVisibleTime],
  );

  useEffect(() => {
    if (!flow || flowRunId == null) {
      emitFlowExit('flow_unloaded');
      return;
    }

    const uniqueFlowKey = `${resolvedFlowId ?? 'unknown'}::${resolvedFlowName ?? 'unknown'}::${flowRunId ?? 'unknown'}`;
    if (
      activeFlowSessionKeyRef.current &&
      activeFlowSessionKeyRef.current !== uniqueFlowKey
    ) {
      emitFlowExit('flow_changed');
    }

    if (flowEntryTrackedRef.current === uniqueFlowKey) {
      if (!activeFlowSessionKeyRef.current) {
        activeFlowSessionKeyRef.current = uniqueFlowKey;
        exitedFlowSessionKeyRef.current = null;
        visibilityStateRef.current =
          typeof document !== 'undefined' ? document.visibilityState : 'visible';
        visibleWindowStartedAtRef.current =
          visibilityStateRef.current === 'visible' ? Date.now() : null;
      }
      return;
    }

    flowEntryTrackedRef.current = uniqueFlowKey;
    activeFlowSessionKeyRef.current = uniqueFlowKey;
    exitedFlowSessionKeyRef.current = null;
    lastTrackedVisibleStepKeyRef.current = null;
    flowStartTimeRef.current = Date.now();
    lastQuestionIdRef.current = null;
    questionStartTimeRef.current = Date.now();
    visibilityStateRef.current =
      typeof document !== 'undefined' ? document.visibilityState : 'visible';
    visibleWindowStartedAtRef.current =
      visibilityStateRef.current === 'visible' ? Date.now() : null;

    track('enter_flow', {
      flowId: resolvedFlowId,
      flowName: resolvedFlowName,
      currentQuestionIndex,
      flowRunId,
    });
  }, [
    currentQuestionIndex,
    emitFlowExit,
    flow,
    flowRunId,
    resolvedFlowId,
    resolvedFlowName,
    track,
  ]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (!activeFlowSessionKeyRef.current || visibilityStateRef.current !== 'visible') {
        return;
      }
      flushActiveVisibleTime('flow_heartbeat');
    }, FLOW_HEARTBEAT_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [flushActiveVisibleTime]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const handleVisibilityChange = () => {
      if (!activeFlowSessionKeyRef.current) {
        return;
      }

      const nextState = document.visibilityState;
      const wasVisible = visibilityStateRef.current === 'visible';
      const isVisible = nextState === 'visible';

      if (wasVisible && !isVisible) {
        flushActiveVisibleTime('flow_visibility_changed', { visibilityState: 'hidden' });
        visibilityStateRef.current = nextState;
        visibleWindowStartedAtRef.current = null;
        return;
      }

      if (!wasVisible && isVisible) {
        visibilityStateRef.current = nextState;
        visibleWindowStartedAtRef.current = Date.now();
        track('flow_visibility_changed', {
          ...getFlowEventMetadata(),
          visibilityState: 'visible',
          activeVisibleMs: 0,
        });
        track('flow_resume', {
          ...getFlowEventMetadata(),
          reason: 'visibility_change',
        });
        return;
      }

      visibilityStateRef.current = nextState;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [flushActiveVisibleTime, getFlowEventMetadata, track]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handlePageHide = () => {
      emitFlowExit('page_hide');
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
    };
  }, [emitFlowExit]);

  useEffect(
    () => () => {
      emitFlowExit('component_unmount');
    },
    [emitFlowExit],
  );

  const markQuestionStart = useCallback((questionId: number | null) => {
    if (lastQuestionIdRef.current !== questionId) {
      lastQuestionIdRef.current = questionId;
      questionStartTimeRef.current = Date.now();
    }
  }, []);

  const getQuestionDuration = useCallback(() => {
    return toMillisSince(questionStartTimeRef);
  }, []);

  const evaluateSpeedWarning = useCallback(
    ({ isAdvancing, timeSpentMs, isStaff }: EvaluateSpeedWarningParams) => {
      let nextFastAnswerCount = consecutiveFastAnswersRef.current;

      if (isAdvancing) {
        const isFastAnswer = timeSpentMs < FAST_ANSWER_THRESHOLD_MS;
        nextFastAnswerCount = isFastAnswer ? consecutiveFastAnswersRef.current + 1 : 0;
      }

      const shouldWarn =
        !isStaff && isAdvancing && nextFastAnswerCount >= FAST_ANSWER_STREAK_TARGET;

      consecutiveFastAnswersRef.current = shouldWarn ? 0 : nextFastAnswerCount;
      return shouldWarn;
    },
    [],
  );

  const trackFlowStepView = useCallback(
    ({ questionId, questionIndex, questionText }: { questionId: number; questionIndex: number; questionText?: string | null }) => {
      if (!activeFlowSessionKeyRef.current || questionIndex < 0) {
        return;
      }

      const visibleStepKey = `${activeFlowSessionKeyRef.current}::${questionId}::${questionIndex}`;
      if (lastTrackedVisibleStepKeyRef.current === visibleStepKey) {
        return;
      }

      lastTrackedVisibleStepKeyRef.current = visibleStepKey;

      const metadata: Record<string, any> = {
        flowId: resolvedFlowId,
        flowName: resolvedFlowName,
        flowRunId,
        questionId,
        questionIndex,
      };

      if (questionText) {
        metadata.questionText = questionText;
      }

      track('flow_step_view', metadata);
    },
    [flowRunId, resolvedFlowId, resolvedFlowName, track],
  );

  const trackFlowContinue = useCallback(
    ({ questionId, targetQuestionIndex, currentQuestionIndex, questionText, timeSpentMs }: FlowContinueEventParams) => {
      const metadata: Record<string, any> = {
        flowId: resolvedFlowId,
        flowName: resolvedFlowName,
        flowRunId,
        questionId,
        questionIndex: targetQuestionIndex,
        currentQuestionIndex,
        timeSpentMs,
      };

      if (questionText) {
        metadata.questionText = questionText;
      }

      track('flow_continue', metadata);
    },
    [flowRunId, resolvedFlowId, resolvedFlowName, track],
  );

  const trackFlowSubmit = useCallback(
    ({
      response,
      targetQuestionIndex,
      totalQuestions,
      answeredQuestions,
      timeSpentMs,
      currentQuestionIndex,
    }: FlowSubmitEventParams) => {
      const submissionMetadata: Record<string, any> = {
        flowId: resolvedFlowId,
        flowName: resolvedFlowName,
        flowRunId,
        totalQuestions,
        answeredQuestions,
        finalQuestionId: response.question,
        finalQuestionIndex: targetQuestionIndex,
        finalQuestionTimeSpentMs: timeSpentMs,
        currentQuestionIndex,
      };

      const flowDurationMs =
        flowStartTimeRef.current != null
          ? Math.max(Date.now() - flowStartTimeRef.current, 0)
          : null;

      if (flowDurationMs != null) {
        submissionMetadata.flowDurationMs = flowDurationMs;
      }

      track('flow_submit', submissionMetadata);
    },
    [flowRunId, resolvedFlowId, resolvedFlowName, track],
  );

  const trackFlowPrevious = useCallback(
    ({
      questionId,
      questionIndex,
      currentQuestionIndex,
      questionText,
      timeSpentMs,
    }: FlowPreviousEventParams) => {
      const metadata: Record<string, any> = {
        flowId: resolvedFlowId,
        flowName: resolvedFlowName,
        flowRunId,
        questionId,
        questionIndex,
        currentQuestionIndex,
        timeSpentMs,
      };

      if (questionText) {
        metadata.questionText = questionText;
      }

      track('flow_previous', metadata);
    },
    [flowRunId, resolvedFlowId, resolvedFlowName, track],
  );

  return useMemo(
    () => ({
      markQuestionStart,
      getQuestionDuration,
      evaluateSpeedWarning,
      trackFlowStepView,
      trackFlowContinue,
      trackFlowSubmit,
      trackFlowPrevious,
    }),
    [
      evaluateSpeedWarning,
      getQuestionDuration,
      markQuestionStart,
      trackFlowStepView,
      trackFlowContinue,
      trackFlowPrevious,
      trackFlowSubmit,
    ],
  );
};
